import { RequestFailModal } from "../../certificates/components/RequestFailModal";
import { RequestSentModal } from "../../certificates/components/RequestSentModal";
import Header from "../../shared/app-shell/Header";
import Sidebar from "../../shared/app-shell/Sidebar";
import { requestedDocsAtom, themeAtom, userAtom } from "../../shared/atoms";
import Breadcrumb from "../../shared/components/Breadcrumb";
import HeaderTitle from "../../shared/components/HeaderTitle";
import { toastError } from "../../shared/components/ToastContainer";
import { log, logV } from "../../shared/services/debug-logger";
import SelectDocumentsStep from "../components/RequestDocuments/SelectDocumentsStep";
import SelectionConfirmStep from "../components/RequestDocuments/SelectionConfirmStep";
import { REQUEST_DOCUMENT_TOTAL_STEPS } from "../models/document-request.constants";
import { Vessel } from "../models/documents.models";
import { requestDocumentsForVessel } from "../services/documents.service";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const RequestDocumentsPage = () => {
  const theme = useAtomValue(themeAtom);

  const [requestedDocs, setRequestedDocs] = useAtom(requestedDocsAtom);
  const [step, setStep] = useState<1 | 2>(1);
  const [isSending, setIsSending] = useState(false);
  const requestInProgress = useRef(false);
  const user = useAtomValue(userAtom);
  const [searchParams] = useSearchParams();
  const vesselName = searchParams.get("vesselName") ?? "";
  const vesselCode = searchParams.get("vesselCode") ?? "";
  const imoNumber = searchParams.get("imoNumber") ?? "";
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  logV("RequestDocumentsPage - BUILD", { step, count: requestedDocs.length, sending: isSending });

  // All params are passed via route, no need to fetch from BE
  const vessel: Vessel = {
    name: vesselName,
    code: vesselCode,
    imoNumber,
  };

  useEffect(() => {
    return () => {
      setRequestedDocs([]);
    };
  }, [setRequestedDocs]);

  const handleNextStep = async () => {
    if (requestInProgress.current || isModalVisible) return;
    log("RequestDocumentsPage - handleNextStep", { step, count: requestedDocs.length });
    if (requestedDocs.length === 0) {
      toastError("At least one document must be selected");
      return;
    }
    if (step === 2) {
      requestInProgress.current = true;
      setIsSending(true);
      try {
        const err = await requestDocumentsForVessel(requestedDocs, user, vessel);
        setError(err);
        log("RequestDocumentsPage - handleNextStep - confirmation", { failed: !!err });
        setIsModalVisible(true);
      } finally {
        requestInProgress.current = false;
        setIsSending(false);
      }
      return;
    }
    setStep(2);
  };

  const handlePrevStep = () => {
    log("RequestDocumentsPage - handlePrevStep");
    setStep(1);
  };

  return (
    <div className="flex h-screen">
      <Sidebar />

      <div
        className={twMerge(
          "flex flex-1 flex-col overflow-y-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />

        <div className="flex flex-1 flex-col overflow-y-hidden">
          {/* Subheader */}
          <div className="mb-[32px] flex flex-col gap-[33px] px-6">
            <HeaderTitle title={"Request document"} />
            <Breadcrumb currentStep={step} totalSteps={REQUEST_DOCUMENT_TOTAL_STEPS} />
          </div>

          {/* Multistep */}
          <div className="flex flex-1 overflow-y-hidden px-6">
            {step === 1 && <SelectDocumentsStep />}
            {step === 2 && <SelectionConfirmStep vessel={vessel} isSending={isSending} />}
          </div>

          {/* Footer */}
          <div className={twMerge("mt-auto flex h-[82px] w-full items-center border-t", theme.uwhcAssistant.border)}>
            {step === 1 && (
              <button
                onClick={() => navigate("/certificates")}
                className={twMerge(
                  "ml-10 rounded border px-4 py-2 text-sm text-[#1F8475]",
                  theme.headerComponent.notifications.itemBackground,
                  theme.headerComponent.logInCircle.text,
                  theme.uwhcAssistant.border,
                )}
              >
                Cancel and exit
              </button>
            )}

            {step > 1 && (
              <button
                onClick={handlePrevStep}
                disabled={isSending || isModalVisible}
                className={twMerge(
                  "ml-auto mr-6 rounded border px-4 py-2 text-sm",
                  theme.headerComponent.notifications.itemBackground,
                  theme.headerComponent.logInCircle.text,
                  theme.uwhcAssistant.border,
                )}
              >
                Previous
              </button>
            )}

            <button
              onClick={handleNextStep}
              disabled={isSending || isModalVisible}
              aria-busy={isSending}
              className={twMerge(
                "mr-10 rounded border bg-[#1F8475] px-4 py-2 text-sm",
                step === 1 && "ml-auto",
                theme.uwhcAssistant.chatBorder,
                theme.warningFunction.text,
              )}
            >
              {isSending ? "Sending..." : "Continue"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ zIndex: "200" }}>
        {isModalVisible &&
          (!error ? (
            <RequestSentModal
              isModalOpen={isModalVisible}
              title="Request sent succesfully"
              caption={
                <span>
                  The request was sent to the corresponding vessel's emails. You should{" "}
                  <b>receive the documents in your inbox</b> in the next following days.
                </span>
              }
              onDismiss={() => navigate("/certificates")}
            />
          ) : (
            <RequestFailModal
              isModalOpen={isModalVisible}
              title="Request failed"
              caption="It seems like there was an issue. Please try again. If the issue persists, feel free to contact support for assistance."
              onDismiss={() => setIsModalVisible(false)}
            />
          ))}
      </div>
    </div>
  );
};

export default RequestDocumentsPage;
